import { useEffect, useState } from "react";
import { printer, share } from "../../../asset";
import FileUpload from "../../../components/common/FileUpload";
import CustomButton from "../../../components/CustomButton";
import CustomInput from "../../../components/CustomInput";
import CustomSelect from "../../../components/CustomSelect";
import SubHeadings from "../../../components/SubHeadings";
import { Student } from "../../../types/studentType";
import { useFormEdit } from "../../../util/hooks/useFormEdit";
import ShareLinksPopup from "../../../components/modal/ShareLinksPopup";
import { useBranchCode } from "../../../util/hooks/useBranch";
import ClassSelect from "../../../components/common-input/ClassSelect";
import StudentApis from "../../../api/studentApis";
import { toast } from "sonner";

interface Props {
  form: ReturnType<typeof useFormEdit<Student>>;
  onPrint: () => void;
}

export default function StudentAdmissionProfile({ form, onPrint }: Props) {
  const { formData, setFormData, isEditing } = form;
  const [showShareModal, setShowShareModal] = useState(false);
  const branchCode = useBranchCode();
  const [logoChanged, setLogoChanged] = useState(false);
  const [profileUrl, setProfileUrl] = useState(formData.photoUrl ?? "");

  useEffect(() => {
    if (formData.photoUrl) {
      setProfileUrl(formData.photoUrl);
    }
  }, [formData.photoUrl]);

  const admissionFormLink = `${window.location.origin}/student-profile/${branchCode}/admission-details/${formData.admissionNumber}`;

  const handleInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = event.target;

    let newValue: any = value;

    if (type === "radio") {
      if (value === "true") newValue = true;
      if (value === "false") newValue = false;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleLogoUpload = (url: string) => {
    const cleanUrl = url.replace(/^"+|"+$/g, "");

    setProfileUrl(cleanUrl);
    setLogoChanged(true);

    setFormData((prev) => ({
      ...prev,
      photoUrl: cleanUrl,
    }));
  };

  const handleSaveLogo = async () => {
    if (!formData?.admissionNumber || !profileUrl) return;

    try {
      await StudentApis.changeStudentProfile(
        formData.admissionNumber,
        profileUrl,
      );
      toast.success("Image updated successfully");
      setLogoChanged(false);
    } catch {
      toast.error("Failed to update Image");
    }
  };

  return (
    <div className="px-4 md:px-9 flex flex-col-reverse lg:flex-row md:divide-x divide-neutral-50 gap-9">
      <div className="flex flex-col gap-7 w-full py-7">
        <div className="flex flex-col gap-2 md:flex-row md:justify-between md:items-center">
          <SubHeadings text="Student’s Profile" />

          <div className="flex gap-2 justify-end items-end">
            <CustomButton
              color="bg-success-600"
              icon={share}
              text="Share form with parents"
              onClick={() => setShowShareModal(true)}
            />
            <CustomButton
              color="bg-success-900"
              icon={printer}
              text="Print/Download"
              onClick={onPrint}
            />
          </div>
        </div>
        {showShareModal && (
          <ShareLinksPopup
            title="Student Admission Form"
            description="Share this link with parents to complete the admission form."
            link={admissionFormLink}
            onClose={() => setShowShareModal(false)}
          />
        )}

        <form className="grid gap-7">
          <div className="grid lg:grid-cols-2 gap-7">
            <CustomInput
              name="fullName"
              label="Student name"
              readOnly={!isEditing}
              type="text"
              placeholder="Enter student name"
              value={formData.fullName ?? ""}
              onChange={handleInputChange}
              inputClassName="w-full"
            />

            <CustomInput
              name="dateOfBirth"
              label="Date of birth"
              type="date"
              readOnly={!isEditing}
              value={formData.dateOfBirth ?? ""}
              onChange={handleInputChange}
              inputClassName="w-full"
            />

            <ClassSelect
              name="classId"
              label="Admission for class"
              readOnly={!isEditing}
              value={formData.classId ?? ""}
              placeholder="Select Class"
              onChange={handleInputChange}
            />

            <CustomSelect
              name="admissionYear"
              label="Admission for academic year"
              readOnly={!isEditing}
              value={formData.admissionYear ?? ""}
              options={[
                { label: "2026-27", value: 2026 },
                { label: "2027-28", value: 2027 },
                { label: "2028-29", value: 2028 },
              ]}
              placeholder="Select admission Year"
              onChange={handleInputChange}
            />
          </div>

          <div className="flex flex-col md:flex-row gap-10 md:items-center w-full">
            <div className="flex gap-7">
              <label className="text-sm font-semibold text-neutral-400 block">
                Gender
              </label>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <CustomInput
                    name="gender"
                    type="radio"
                    checked={formData.gender === "MALE"}
                    value="MALE"
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    inputClassName="w-3 h-3"
                  />
                  <span
                    className={`text-sm font-semibold ${
                      formData.gender === "MALE"
                        ? "text-primary-500"
                        : "text-neutral-300"
                    }`}
                  >
                    Male
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <CustomInput
                    name="gender"
                    type="radio"
                    checked={formData.gender === "FEMALE"}
                    value="FEMALE"
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    inputClassName="w-3 h-3"
                  />
                  <span
                    className={`text-sm font-semibold ${
                      formData.gender === "FEMALE"
                        ? "text-primary-500"
                        : "text-neutral-300"
                    }`}
                  >
                    Female
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-7">
              {/* <label className="text-sm font-semibold text-neutral-400 block">
                Opt for daycare?
              </label> */}
              {/* <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <CustomInput
                    name="optForDaycare"
                    type="radio"
                    checked={formData.optForDaycare === true}
                    value="true"
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    inputClassName="w-3 h-3"
                  />
                  <span
                    className={`text-sm font-semibold ${
                      formData.optForDaycare === true
                        ? "text-primary-500"
                        : "text-neutral-300"
                    }`}
                  >
                    yes
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <CustomInput
                    name="optForDaycare"
                    type="radio"
                    checked={formData.optForDaycare === false}
                    value="false"
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    inputClassName="w-3 h-3"
                  />
                  <span
                    className={`text-sm font-semibold ${
                      formData.optForDaycare === false
                        ? "text-primary-500"
                        : "text-neutral-300"
                    }`}
                  >
                    No
                  </span>
                </div>
              </div> */}
            </div>
          </div>
          <div className="grid lg:grid-cols-2 gap-7">
            <CustomInput
              name="previousSchoolName"
              label="Previous school name"
              type="text"
              placeholder="Enter Previous school name"
              readOnly={!isEditing}
              value={formData.previousSchoolName ?? ""}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
            <CustomSelect
              name="studentCategory"
              label="Student Category"
              value={formData.studentCategory}
              readOnly={!isEditing}
              options={[
                { label: "General", value: "GENERAL" },
                { label: "OBC", value: "OBC" },
                { label: "SC", value: "SC" },
                { label: "ST", value: "ST" },
              ]}
              placeholder="Select student category"
              onChange={handleInputChange}
            />
          </div>
        </form>
      </div>

      <div className="md:pl-7 pt-7 md:py-7">
        <FileUpload
          value={profileUrl}
          onUploadComplete={handleLogoUpload}
          className="md:w-52 w-36 aspect-square"
          disabled={!isEditing}
          showButton={!logoChanged}
          buttonText="Upload Image"
          uploadText="Change Image"
        />

        {isEditing && logoChanged && (
          <button
            type="button"
            onClick={handleSaveLogo}
            className="bg-primary-500 text-white rounded-lg py-2 px-4 w-full text-sm font-semibold mt-3"
          >
            Save Image
          </button>
        )}
      </div>
    </div>
  );
}
